import axios, { AxiosResponse, isAxiosError } from 'axios';
import { getCountryStates } from '@ikea-customer-self-serve/contact-form';
import { Errors } from './errors';
import {
  LocationServiceResponse,
  PostalCodeLocation,
  AddressComponents,
  PostalCodeLookup,
  GeoCodes,
} from './model';

/* global CSS_CONFIG */
const {
  COUNTRY,
  GETSTATES: { useStateNameAbbreviation, ingkaLocationServiceApi, apiSrc },
  DEFAULTLANGUAGE,
  LANGUAGE,
  SERVICES: { getServicesUsingStateCode },
} = CSS_CONFIG;

class State {
  private getFallbackLocations(): PostalCodeLocation[] {
    // Populate with fallback locations for countries requiring a state code
    // in get services request.
    return getServicesUsingStateCode
      ? getCountryStates(COUNTRY).map((state) => {
          return { state };
        })
      : [];
  }

  private async getStateByLocationServiceAPI(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    try {
      const result: AxiosResponse<LocationServiceResponse> = await axios.get(
        `${ingkaLocationServiceApi}/locationservice/${COUNTRY}/${postalCode}`,
      );
      let locations: PostalCodeLocation[] = [];

      if (COUNTRY === 'au') {
        const { additionalAttributes } = result.data;
        locations = (additionalAttributes?.stateSuburbRelation || []).map(
          (stateSuburb) => {
            return { state: stateSuburb.state, city: stateSuburb.city };
          },
        );
        return { locations, valid: true };
      }

      const { additionalAttributes } = result.data;
      locations = (additionalAttributes?.stateInfo || []).map((info) => {
        const state = useStateNameAbbreviation
          ? info.stateCode
          : info.stateName || info.stateCode;
        return { state };
      });

      // Invalid postal code case 1.
      // US returns status 200 and validZipCode set to false for invalid postal code.
      const { validZipCode } = result.data;
      const valid = validZipCode !== undefined ? validZipCode : true;

      return { locations, valid };
    } catch (err) {
      if (isAxiosError(err)) {
        const { response } = err;
        // Invalid postal code case 2.
        // AU and CA returns status 400 for invalid postal code.
        if (response?.status === Errors.INVALID_POSTAL_CODE) {
          return { locations: [], valid: false };
        }
      }

      return { locations: this.getFallbackLocations(), valid: true };
    }
  }

  private filterOutIrrelevantMarketsGeoCodes = (data: GeoCodes) => {
    for (const result of data.results) {
      for (const component of result.address_components) {
        const isItCountryComponent = component.types.includes('country');
        if (
          isItCountryComponent &&
          component.short_name.toLowerCase() === COUNTRY.toLowerCase()
        ) {
          return result;
        }
      }
    }
  };

  private async getStateByGoogleGeocodeAPI(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    try {
      const url = `${CSS_CONFIG.API}/geocode/${postalCode}`;
      const headers = {
        'country-language': `${COUNTRY}-${DEFAULTLANGUAGE || LANGUAGE}`,
      };
      const { data }: { data: GeoCodes } = await axios.get(url, {
        headers,
      });
      const geocode = this.filterOutIrrelevantMarketsGeoCodes(data);

      if (!geocode) {
        return { locations: this.getFallbackLocations(), valid: true };
      }
      const addressComponents = geocode.address_components;
      const [localArray] = (addressComponents || []).filter(
        (item: AddressComponents) =>
          item.types.some((type: string) => type === 'locality'),
      );
      const [stateArray] = (addressComponents || []).filter(
        (item: AddressComponents) =>
          item.types.some(
            (type: string) => type === 'administrative_area_level_1',
          ),
      );

      const city = localArray?.long_name;
      const state = useStateNameAbbreviation
        ? stateArray.short_name
        : stateArray.long_name;

      return state
        ? { locations: [{ city, state }], valid: true }
        : { locations: this.getFallbackLocations(), valid: true };
    } catch (err) {
      return { locations: this.getFallbackLocations(), valid: true };
    }
  }

  public async getPostalCodeLocations(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    if (apiSrc === 'GOOGLE') {
      return this.getStateByGoogleGeocodeAPI(postalCode);
    }

    if (apiSrc === 'INGKA_LOCATION') {
      return this.getStateByLocationServiceAPI(postalCode);
    }

    return Promise.resolve({ locations: [], valid: true });
  }
}

export default State;
